import { useRef, type ReactNode } from "react";
import { styled } from "@pigment-css/react";
import { useBottomNavClearance } from "@acto/taskpane/field-sales-2/components/primitives/useBottomNavClearance";

const ShellRoot = styled("div")(({ theme }) => ({
  position: "fixed",
  // Anchor both edges rather than width:100%. A fixed element's 100% resolves against the
  // initial containing block, which includes a reserved scrollbar gutter (window.innerWidth),
  // so on a gutter-reserving page it overflows the visible width and forces a horizontal scroll.
  // left+right span the content area, gutter-safe.
  left: 0,
  right: 0,
  bottom: 0,
  // Above page chrome (analysis dock 8 / sticky header 7 / SelectMenu switcher 6) so the nav
  // never renders behind them, but below the modal/overlay band (popovers 1000, sheets/
  // PeriodPicker 1101, toasts 1200, full-screen modals 1300) which must still cover the nav.
  zIndex: 9,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[16],
  padding: `0 ${theme.fieldSalesTwo.spacing[12]} ${theme.fieldSalesTwo.spacing[32]}`,
  background: `linear-gradient(to top, ${theme.fieldSalesTwo.colors.background} 26.5%, rgba(240,238,237,0))`,
  pointerEvents: "none",
  "& > *": {
    pointerEvents: "auto",
  },
  // A focused field raises the on-screen keyboard, and this fixed shell would cover it. visibility,
  // not display, keeps the box useBottomNavClearance measures, so the page does not change height
  // when the field blurs.
  "@media (pointer: coarse)": {
    "body:has([data-keyboard-field]:focus-within) &": {
      visibility: "hidden",
    },
  },
}));

interface BottomNavShellProps {
  children: ReactNode;
}

export function BottomNavShell({ children }: BottomNavShellProps) {
  const ref = useRef<HTMLDivElement>(null);
  useBottomNavClearance(ref);

  return <ShellRoot ref={ref}>{children}</ShellRoot>;
}
